import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { ExportViewProps, ExportViewRef } from "./models";
import SearchFilter, { Filters } from "../../../Common/SearchFilter/SearchFilter";
import { searchFilterToExportExcelFilter } from "../../../Common/SearchFilter/utils";
import useDidMountEffect from "../../../Common/UseDidMountEffect";
import { ApiBackend } from "../../../../providers/apibackend";
import { Event } from "../../../../model/Event";
import { Product } from "../../../../model/Product";

type SearchFilterType = {
    raceWeek?: string[];
    discipline?: string;
    grouping?: string;
    minAge?: number;
    okForCommercialFilter?: boolean;
    okForThirdPartyFilter?: boolean;
    onlyNational?: boolean;
    onlyRaces?: string[];
    notEnteredEventId?: string;
};

interface ISearchFilter {
    raceWeek?: string[];
    discipline?: string;
    grouping?: string;
    minAge?: number;
    okForCommercialFilter?: boolean;
    okForThirdPartyFilter?: boolean;
    onlyRaces?: string[];
    notEnteredEventId?: string;
}

const EnteredForRaceWeek = forwardRef<ExportViewRef, ExportViewProps>((props, ref) => {
    const [searchFilter, setSearchFilter] = useState(null as ISearchFilter);
    const [events, setEvents] = useState(null as Event[]);
    const [races, setRaces] = useState(null as Product[]);
    useImperativeHandle(ref, () => ({
        isValid: () => {
            // Minst ett evenemang måste väljas. Disciplin är valfritt.
            return !!searchFilter?.raceWeek && searchFilter.raceWeek.length > 0;
        }
    }));

    useEffect(() => {
        let backend = new ApiBackend();
        backend.listEvents().then((res) => {
            setEvents(res);
        });
        backend.listProducts().then((res) => {
            setRaces(res);
        });
    }, []);

    const disciplines = [{ name: "Skidor", value: "ski" }, { name: "Cykel", value: 'bike' }, { name: 'Löpning', value: 'run' }];

    useDidMountEffect(() => {
        props.onChange(searchFilterToExportExcelFilter<ISearchFilter>(searchFilter));

    }, [searchFilter]);

    const render = () => {

        // "Enbart lopp" går bara att filtrera på när exakt ETT evenemang är valt.
        const selectedEventIds = searchFilter?.raceWeek || [];
        const singleEventId = selectedEventIds.length === 1 ? selectedEventIds[0] : null;
        const selRaces = singleEventId ? races?.filter(x => x.EventId == singleEventId && x.type == "race") : [];
        const eventItems = events == null ? [] : events.map(x => { return { name: x.name, value: x.id } });

        return (
            <SearchFilter<SearchFilterType>
                id={"export-enteredforraceweek-filter"}
                filters={{
                    "raceWeek": {
                        id: "filter-raceWeek",
                        type: "GenericSelect",
                        label: "Evenemang (ett eller flera)",
                        size: 12,
                        multiple: true,
                        itemDefinition: {
                            items: eventItems,
                            itemValueType: "string"
                        }
                    },
                    "discipline": {
                        id: "filter-discipline",
                        type: "GenericSelect",
                        label: "Diciplin (valfritt)",
                        size: 12,
                        multiple: false,
                        clearable: true,
                        itemDefinition: {
                            items: disciplines == null ? [] : disciplines.map(x => { return { name: x.name, value: x.value } }),
                            itemValueType: "string"
                        }
                    },
                    "grouping": {
                        id: "filter-grouping",
                        type: "GenericSelect",
                        label: "Gruppering",
                        size: 12,
                        multiple: false,
                        defaultValue: "personPerRace",
                        itemDefinition: {
                            items: [
                                { name: "Person per lopp (en rad per person och lopp)", value: "personPerRace" },
                                { name: "Ingen (en rad per anmälan)", value: "none" },
                                { name: "E-post per lopp (en rad per e-post och lopp)", value: "emailPerRace" },
                                { name: "E-post globalt (en rad per e-post)", value: "emailGlobal" }
                            ],
                            itemValueType: "string"
                        }
                    },
                    "okForCommercialFilter": {
                        id: "filter-okForCommercialFilter",
                        type: "Checkbox",
                        label: "Applicera filter ok med reklam",
                        size: 12,
                        defaultValue: false,
                        multiple: false
                    },
                    "okForThirdPartyFilter": {
                        id: "filter-okForThirdPartyFilter",
                        type: "Checkbox",
                        label: "Applicera filter ok med 3:e part",
                        size: 12,
                        defaultValue: false,
                        multiple: false
                    },
                    "minAge": {
                        id: "filter-minAge",
                        type: "GenericSelect",
                        label: "Filter minsta ålder (valfritt)",
                        size: 12,
                        multiple: false,
                        itemDefinition: {
                            items: [16, 18].map(x => { return { name: x + '', value: x } }),
                            itemValueType: "number"
                        }
                    },
                    "onlyNational": {
                        id: "filter-onlyNational",
                        type: "Checkbox",
                        label: "Enbart nationella (valfritt)",
                        size: 12,
                        defaultValue: false,
                        multiple: false
                    },
                    "onlyRaces": {
                        id: "filter-onalyraces",
                        type: "GenericSelect",
                        label: singleEventId ? "Enbart lopp (valfritt)" : "Enbart lopp (endast när ett evenemang är valt)",
                        size: 12,
                        multiple: true,
                        itemDefinition: {
                            items: selRaces == null ? [] : selRaces.map(x => { return { name: x.name + '', value: x.id } }),
                            itemValueType: "string"
                        }
                    },
                    "notEnteredEventId": {
                        id: "filter-notenteredevent",
                        type: "GenericSelect",
                        label: "Person ej anmäld till evenemang (valfritt)",
                        size: 12,
                        multiple: false,
                        itemDefinition: {
                            items: eventItems,
                            itemValueType: "string"
                        }
                    },
                }}
                persist={true}
                onInit={(filter: Filters<SearchFilterType>) => {
                    setSearchFilter(filter as unknown as ISearchFilter);
                }}
                onChange={(filter: Filters<SearchFilterType>) => {
                    setSearchFilter(filter as unknown as ISearchFilter);
                }}
            />
        );
    };

    return render();
});

export default EnteredForRaceWeek;
